import { Home, Building, Layout, Hammer, Wrench, Map, Shield, User } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description, className }) => (
  <div className={cn(
    "seafarer-card p-6 hover:shadow-lg transition-all duration-300 animate-fade-in",
    className
  )}>
    <div className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-br from-seafarer-orange to-seafarer-purple text-white mb-5">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-3">{title}</h3>
    <p className="text-gray-600 dark:text-gray-300">{description}</p>
  </div>
);

const ServicesSection = () => {
  const services = [
    {
      icon: <Home className="h-6 w-6" />,
      title: "Property Development",
      description: "We develop affordable, quality housing to address the needs of fishermen and harbor communities."
    },
    {
      icon: <Building className="h-6 w-6" />,
      title: "Project Management",
      description: "Expert management of construction projects from planning to completion."
    },
    {
      icon: <Layout className="h-6 w-6" />,
      title: "Architectural Design",
      description: "Custom architectural solutions that blend functionality with aesthetic appeal."
    },
    {
      icon: <Hammer className="h-6 w-6" />,
      title: "Construction Services",
      description: "Quality construction work delivered on time and within budget."
    },
    {
      icon: <Wrench className="h-6 w-6" />,
      title: "Property Maintenance",
      description: "Comprehensive maintenance services to keep properties in excellent condition."
    },
    {
      icon: <Map className="h-6 w-6" />,
      title: "Land Development",
      description: "Land Development has started in 2024 to 2025.",
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Health & Safety Management",
      description: "Implementation of rigorous health and safety standards on all projects."
    },
    {
      icon: <User className="h-6 w-6" />,
      title: "Community Support",
      description: "Programs designed to strengthen and empower harbor town communities."
    },
  ];

  return (
    <section className="section-padding">
      <div className="page-container">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Our Services</h2>
          <p className="text-lg text-white">
            We offer a comprehensive range of services to support our mission of providing
            quality housing and improving the welfare of fishermen and their families.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={service.title}
              icon={service.icon}
              title={service.title}
              description={service.description}
              className={`[animation-delay:${index * 100}ms]`}
            />
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <Button asChild className="seafarer-btn-primary">
            <Link to="/services">View All Services</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
